import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { trackPageView } from "./utils/analytics";
import { Box, useTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import Home from "./pages/Home";
import DealDetails from "./components/deals/DealDetails";
import Login from "./components/auth/Login";
import Signup from "./components/auth/Signup";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ScrollToTop from "./components/layout/ScrollToTop";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import Disclaimer from "./pages/Disclaimer";
import About from "./pages/About";
import Contact from "./pages/Contact";
import DealsTabsContainer from "./components/deals/DealsTabsContainer";
import RestaurantDeals from "./pages/RestaurantDeals";
import YoursRestaurantDealsPage from "./pages/YoursRestaurantDealsPage";
import TelegramReminderSetup from "./components/reminders/TelegramReminderSetup";
import ReminderSetupPromptHost from "./components/reminders/ReminderSetupPromptHost";
import { AnimatePresence } from "framer-motion";

function AppContent() {
  const theme = useTheme();
  const location = useLocation();

  /** Keeps `main` below fixed Navbar (includes notch safe area): safe + Navbar pt + Navbar pb + ~row height. */
  const mainNavOffsetSx = {
    xs: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(2)} + ${theme.spacing(2)} + 48px)`,
    md: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)} + ${theme.spacing(3)} + 48px)`,
  };

  useEffect(() => {
    const path = location.pathname + location.search;
    trackPageView(path);
  }, [location.pathname, location.search]);

  const isDealsSection =
    location.pathname === "/deals" || location.pathname.startsWith("/deals/");
  const isDealDetailsPage = location.pathname.startsWith("/deal/");
  const isRestaurantDealsPage = location.pathname.match(/^\/[^/]+$/) && 
    location.pathname !== "/" && 
    location.pathname !== "/home" &&
    location.pathname !== "/deals" &&
    location.pathname !== "/login" &&
    location.pathname !== "/signup" &&
    location.pathname !== "/forgot-password" &&
    location.pathname !== "/reset-password" &&
    location.pathname !== "/about" &&
    location.pathname !== "/contact" &&
    location.pathname !== "/terms" &&
    location.pathname !== "/privacy" &&
    location.pathname !== "/cookies" &&
    location.pathname !== "/disclaimer" &&
    location.pathname !== "/reminders";
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      {/* Hide Navbar on deals page and deal details page */}
      {!isDealsSection && !isDealDetailsPage && !isRestaurantDealsPage && <Navbar />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: isRestaurantDealsPage ? "hidden" : "auto",
          minHeight: 0, // Important for flex parent
          pt:
            isDealsSection || isDealDetailsPage || isRestaurantDealsPage
              ? 0
              : mainNavOffsetSx,
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route
              path="/deals/yours/brands"
              element={
                <Navigate
                  to="/deals"
                  replace
                  state={{ openRestaurantDrawer: true }}
                />
              }
            />
            <Route
              path="/deals/yours/restaurant/:restaurantName"
              element={<YoursRestaurantDealsPage />}
            />
            <Route path="/deals" element={<DealsTabsContainer />} />
            <Route
              path="/bakeries"
              element={<Navigate to="/deals" replace />}
            />
            <Route path="/deal/:dealId" element={<DealDetails />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/reminders"
              element={<TelegramReminderSetup />}
            />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/:restaurantName" element={<RestaurantDeals />} />
          </Routes>
        </AnimatePresence>
      </Box>

      {!isAuthPage && !isDealsSection && !isDealDetailsPage && !isRestaurantDealsPage && <Footer />}
      <ReminderSetupPromptHost />
    </Box>
  );
}

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>Loops - Find Food Deals Near You</title>
        <meta name="description" content="Discover discounted breakfast, lunch, and dinner deals in Malaysia. Your next affordable meal is just a click away!" />
        <meta property="og:site_name" content="Loops" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://loopsfood.com" />
        <meta property="og:title" content="Loops - Find Food Deals Near You" />
        <meta property="og:description" content="Discover discounted breakfast, lunch, and dinner deals in Malaysia. Your next affordable meal is just a click away!" />
        <meta property="og:image" content="https://loopsfood.com/assets/loops-og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Loops - Find Food Deals Near You" />
        <meta name="twitter:description" content="Discover discounted breakfast, lunch, and dinner deals in Malaysia. Your next affordable meal is just a click away!" />
        <meta name="twitter:image" content="https://loopsfood.com/assets/loops-og-image.png" />
        <link rel="canonical" href="https://loopsfood.com" />
      </Helmet>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <Router>
            <AuthProvider>
              <CartProvider>
                <ScrollToTop />
                <AppContent />
              </CartProvider>
            </AuthProvider>
          </Router>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
